import type {
  AvailabilityByDate,
  AvailabilityBySession,
  AvailabilityType,
  FirebaseAvailability,
} from '@firsttable/types';

export const comparePaths = (currentPath: string, toTest: string) =>
  currentPath.replace(/^\/|\/$/g, '') === toTest.replace(/^\/|\/$/g, '');

/**
 * Handles filtering our city page restaurants
 * Filters by:
 * - city search selected session
 * - selected cuisine
 * - selected other filters e.g. has amex or 4 plus more
 * @param restaurants
 * @param filterSet
 */
export const filteredRestaurants = (restaurants, filterSet, availableOptions = {}) => {
  let { tagFilters, cuisineFilters, filters, session } = filterSet;
  const { cuisineFilters: availableCuisines, filters: availableFilters } = <any>availableOptions;

  cuisineFilters = cuisineFilters.filter(
    (cuisine) => !availableCuisines || availableCuisines.includes(cuisine)
  );

  filters = filters.filter((value) => !availableFilters || availableFilters.includes(value));

  return restaurants.filter((node) => {
    let valid = true;

    // Sessions Filters
    if (session !== 'any') {
      if (session === 'dinner') {
        valid &&= node.sessionTypes.includes('dinner') || node.sessionTypes.includes('dinner2');
      } else {
        valid &&= node.sessionTypes.includes(session);
      }
    }

    // Feature Filters
    if (filters.includes('acceptsAmex')) {
      valid &&= node.acceptsAmex;
    }

    if (filters.includes('hasRegularTable')) {
      valid &&= node.offersRegularTable;
    }

    if (filters.includes('isNew')) {
      valid &&= node.isNew;
    }
    // This can be pre-ticked and then hidden by changing session
    if (session === 'dinner' && filters.includes('hasLastTable')) {
      valid &&= node.sessionTypes.includes('dinner2');
    }

    // Cuisine Filter - Check valid so we don't run expensive mapping if already excluded
    if (valid && cuisineFilters.length) {
      const inCuisines = { ...node.cuisines }.edges.filter((cuisine) =>
        cuisineFilters.includes(cuisine.node.id)
      );
      valid &&= inCuisines.length > 0;
      // AND cuisines instead of OR cuisines; valid && inCuisines.length === cuisineFilters.length;
    }

    // Tag filers designed so we still use restaurant.cuisine
    const tagCategories = Object.entries(tagFilters);
    if (tagCategories.length) {
      tagCategories.forEach(([, category]: [title: string, category: number[]]) => {
        if (valid && category.length) {
          const inCuisines = { ...node.cuisines }.edges.filter((cuisine) =>
            category.includes(cuisine.node.id)
          );
          valid &&= inCuisines.length > 0;
          // AND cuisines instead of OR cuisines; valid && inCuisines.length === cuisineFilters.length;
        }
      });
    }

    return valid;
  });
};

export const allAvailabilityToAvailability = (
  input: FirebaseAvailability // availability is session/date/id
) =>
  Object.entries(input).reduce((acc: AvailabilityBySession, [session, dates]) => {
    // Iterate sessions
    acc[session] = Object.entries(dates).reduce(
      // Iterate dates
      (acc2: AvailabilityByDate, [date, times]) => {
        const allTimes = Object.values(times).sort((a, b) => a?.index - b?.index);

        // Use first available as base
        const first = allTimes.find((i) => i?.available) ?? allTimes[0];
        // Set hasMultipleTimes if there are more than one available times
        const hasMultipleTimes = allTimes.filter((i) => i?.available).length > 1;
        // Build fake time slot with the two extra props
        acc2[date] = {
          ...first,
          hasMultipleTimes,
          allTimes: []
            .concat(allTimes)
            .sort((a, b) => {
              if (!a?.time || !b?.time) {
                return 0;
              }

              return a.time.localeCompare(b.time);
            })
            .map((item) => ({
              ...item,
              isDefault: item === first,
            })),
        };
        return acc2;
      },
      {}
    );
    return acc;
  }, {});

function safeUTCTime(time: string): Date {
  const [datePart, timePart] = time.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = timePart.split(':').map(Number);

  const utcTimestamp = Date.UTC(year, month - 1, day, hours, minutes, seconds);
  return new Date(utcTimestamp);
}

function getHoursAway(availability: AvailabilityType): number {
  if (!availability?.cutOffTime) {
    return 0;
  }

  // const localCutOffDateTime = new Date(`${availability.cutOffTime} UTC`);
  const localCutOffDateTime = safeUTCTime(availability.cutOffTime);
  const nowDateTime = new Date();
  const timeToCutOff = localCutOffDateTime.getTime() - nowDateTime.getTime();

  const msInHour = 1000 * 60 * 60;
  return timeToCutOff / msInHour;
}

export function isPast(availability: AvailabilityType): boolean {
  return availability.cutOffTime && getHoursAway(availability) < 0;
}

export function showCutOffMessage(availability: AvailabilityType): boolean {
  const hoursAway = getHoursAway(availability);
  return (
    hoursAway < 4 && // show if less that 4 hours away
    hoursAway > 0 && // show only if today i.e. not negative hours away
    availability.cutOffTime &&
    availability.available
  );
}

// Helper type for recursive cutoff transformation
export type DeepAvailabilityWithCutOff<T extends AvailabilityType> = T extends {
  allTimes?: infer U extends AvailabilityType[];
}
  ? Omit<T, 'allTimes'> & {
      isPast: boolean;
      showCutOff: boolean;
      allTimes?: DeepAvailabilityWithCutOff<U[number]>[];
    }
  : T & {
      isPast: boolean;
      showCutOff: boolean;
    };

export function withCutoff<T extends AvailabilityType>(
  availability: T & {
    allTimes?: any[];
  }
): DeepAvailabilityWithCutOff<T> {
  const availabilityWithCutoff: any = {
    ...availability,
    isPast: isPast(availability),
    showCutOff: showCutOffMessage(availability),
  };

  if ('allTimes' in availability && Array.isArray(availability.allTimes)) {
    availabilityWithCutoff.allTimes = availability.allTimes.map(withCutoff);
  }

  return availabilityWithCutoff;
}

export function sessionHasMultipleTimes(
  sessionAvailability: AvailabilityByDate,
  datesToShow: string[]
): boolean {
  return datesToShow.some((date) => sessionAvailability[date]?.hasMultipleTimes);
}

/**
 * Calculates the final price for a gift voucher taking into account any discounts
 *
 * @param {Object} values - Form values object containing price information
 * @param {(number|Object)} values.price - Either a number price or an object with price property
 * @param {Object} giftVoucherData - Gift voucher data containing discount information
 * @param {boolean} giftVoucherData.hasDiscount - Whether a discount should be applied
 * @param {number} giftVoucherData.discount - Discount amount as decimal (e.g. 0.2 for 20% off)
 *
 * @returns {number} The calculated final price
 */
export function calculatePrice(
  values: { price: number | { price: number } },
  giftVoucherData: { hasDiscount: boolean; discount: number }
): number {
  const { price } = values;
  const { hasDiscount, discount } = giftVoucherData;

  if (typeof price === 'object' && price?.price) {
    return price.price;
  }

  if (hasDiscount) {
    return Number(price) * (1 - discount);
  }

  return Number(price);
}

export const getDefaultSession = (sessions: string[]) => {
  if (sessions.includes('dinner')) {
    return 'dinner';
  }
  if (sessions.includes('dinner2')) {
    return 'dinner2';
  }
  if (sessions.includes('lunch')) {
    return 'lunch';
  }
  return sessions[0] ?? 'dinner';
};

export function getNormalisedRevenue(revenue: number, price: number, priceInNZD: number) {
  const conversionRate = price !== 0 ? +(priceInNZD ?? price) / +price : 1;
  return (revenue * conversionRate).toFixed(2);
}
